import React, { createContext, useContext, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import Toast from '../components/shared/Toast';
import type { ToastData, ToastPosition } from '../components/shared/Toast';

interface ToastContextType {
  showToast: (options: Omit<ToastData, 'id'>) => string;
  showSuccess: (message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => string;
  showError: (message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => string;
  showWarning: (message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => string;
  showInfo: (message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => string;
  dismissToast: (id: string) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
  maxToasts?: number;
  defaultPosition?: ToastPosition;
  defaultDuration?: number;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  maxToasts = 5,
  defaultPosition = 'bottom-right',
  defaultDuration = 4000
}) => {
  const [toasts, setToasts] = useState<(ToastData & { open: boolean })[]>([]);

  const generateId = useCallback(() => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showToast = useCallback((options: Omit<ToastData, 'id'>): string => {
    const id = generateId();
    const newToast: ToastData & { open: boolean } = {
      id,
      position: defaultPosition,
      duration: defaultDuration,
      ...options,
      open: true
    };

    setToasts(current => {
      // Remove oldest toasts if we exceed maxToasts
      const filtered = current.length >= maxToasts 
        ? current.slice(1) 
        : current;
      
      return [...filtered, newToast];
    });

    return id;
  }, [generateId, defaultPosition, defaultDuration, maxToasts]);

  const showSuccess = useCallback((message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => {
    return showToast({ message, type: 'success', ...options });
  }, [showToast]);

  const showError = useCallback((message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => {
    return showToast({ message, type: 'error', ...options });
  }, [showToast]);

  const showWarning = useCallback((message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => {
    return showToast({ message, type: 'warning', ...options });
  }, [showToast]);

  const showInfo = useCallback((message: string, options?: Partial<Omit<ToastData, 'id' | 'type' | 'message'>>) => {
    return showToast({ message, type: 'info', ...options });
  }, [showToast]);

  const dismissToast = useCallback((id: string) => {
    setToasts(current =>
      current.map(toast =>
        toast.id === id ? { ...toast, open: false } : toast
      )
    );

    // Remove from state after animation completes
    setTimeout(() => {
      setToasts(current => current.filter(toast => toast.id !== id));
    }, 300);
  }, []);

  const dismissAll = useCallback(() => {
    setToasts(current =>
      current.map(toast => ({ ...toast, open: false }))
    );

    // Remove all from state after animation completes
    setTimeout(() => {
      setToasts([]);
    }, 300);
  }, []);

  const contextValue: ToastContextType = {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    dismissToast,
    dismissAll
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      
      {/* Render all toasts */}
      {toasts.map(toast => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={dismissToast}
        />
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
